import { useState, useEffect, useCallback, useRef } from 'react';
import { isSystemAdmin as isSystemAdminCheck } from '@turn-connect/hooks';
import { authService, User, LoginRequest, LoginResponse } from '../services/auth';
import { getApiBaseUrl } from '../utils/config';
import { setStoredLocationId } from '../hooks/useLocationSelection';
import { clearStoredAccountId } from '../hooks/useAccountSelection';
import { originalFetch } from '../utils/fetchInterceptor';
import { arrivedFromSignInLink } from './usePendingPasswordReset';

interface AuthState {
  user: User | null;
  permissions: string[];
  isLoading: boolean;
  isAuthenticated: boolean;
}

export const useAuth = () => {
  // No backwards-compat normalization; expect resource:action from API
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    permissions: [],
    isLoading: true,
    isAuthenticated: false,
  });
  const [error, setError] = useState<string>('');

  // Prevent duplicate auth checks
  const checkingAuthRef = useRef(false);
  const hasCheckedRef = useRef(false);
  // Grace period after login to prevent premature logout from race conditions
  const loginGracePeriodRef = useRef(false);
  // Track if any 401s occurred during grace period - if so, we need to retry auth check after grace ends
  const gracePeriodFailureRef = useRef(false);

  // Check authentication status on mount
  useEffect(() => {
    // Skip if already checked (prevents StrictMode double-mount)
    if (hasCheckedRef.current) {
      return;
    }
    // Set flag immediately before calling checkAuthStatus to prevent race condition
    hasCheckedRef.current = true;
    checkAuthStatus();
  }, []);

  // Auto-logout on unauthorized events dispatched globally from fetch interceptor.
  // Uses a verify-before-logout approach: on first 401, re-check /auth/me to confirm
  // the session is actually dead before clearing state. This prevents transient 401s
  // (e.g., race conditions during navigation) from destroying a valid session.
  useEffect(() => {
    let handlingUnauthorized = false;
    const onUnauthorized = async () => {
      // Skip logout during login grace period to prevent race conditions
      // This handles cases where API calls fire before cookies are fully processed
      if (loginGracePeriodRef.current) {
        // Track that a failure occurred during grace period - we'll retry auth check after grace ends
        gracePeriodFailureRef.current = true;
        console.warn('[useAuth] 401 received during login grace period - will retry auth check after grace period');
        return;
      }

      // Debounce: multiple 401s fire simultaneously, only handle once
      if (handlingUnauthorized) return;
      handlingUnauthorized = true;

      // Verify the session is actually dead before logging out.
      // A single 401 from a data endpoint doesn't mean the session is invalid —
      // it could be a transient issue, permission change, or race condition.
      try {
        const apiBase = getApiBaseUrl();
        const verifyRes = await originalFetch(`${apiBase}/auth/me`, {
          credentials: 'include',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (verifyRes.ok) {
          // Session is still valid — ignore the 401 from the data endpoint
          console.warn('[useAuth] 401 on data endpoint but /auth/me succeeded — session still valid, ignoring');
          setTimeout(() => {
            handlingUnauthorized = false;
          }, 1000);
          return;
        }
      } catch {
        // Network error during verification — treat as session dead
      }

      // Session confirmed dead — clear everything
      const hadStoredData = localStorage.getItem('user_data');

      try {
        localStorage.removeItem('user_data');
        localStorage.removeItem('user_permissions');
        setStoredLocationId(null);
        clearStoredAccountId();
      } catch {}
      setAuthState({ user: null, permissions: [], isLoading: false, isAuthenticated: false });

      // Don't call clearSessionCookie() here — it calls /auth/logout which
      // would invalidate a potentially recoverable session. The server already
      // knows the JWT is invalid (it returned 401). Clearing local state and
      // showing the login screen is sufficient.

      // Only show "session expired" if user was previously logged in
      if (hadStoredData) {
        setError('Session expired. Please log in again.');
      }

      // Reset debounce after a short delay
      setTimeout(() => {
        handlingUnauthorized = false;
      }, 1000);
    };
    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized);
  }, []);

  const checkAuthStatus = async () => {
    // Prevent duplicate concurrent checks
    if (checkingAuthRef.current) {
      return;
    }

    // Set flag immediately to prevent race condition
    checkingAuthRef.current = true;

    try {
      // Don't set loading state here - causes unnecessary re-render
      // setAuthState(prev => ({ ...prev, isLoading: true }));

      // First try to get stored user data
      const storedUserData = localStorage.getItem('user_data');
      const storedPermissions = localStorage.getItem('user_permissions');

      // Short-circuit for fresh unauthenticated visitors:
      // If there's no cached user data, calling /auth/me will 401 (the session
      // cookie is HttpOnly so we can't peek at it from JS, but localStorage is
      // always written alongside the cookie at login time — its absence is a
      // strong "never logged in here" signal). The 401 shows up in DevTools
      // network console, which Lighthouse counts under `errors-in-console`,
      // capping Best Practices. Skip the call; the resulting state is what we
      // would have set anyway. Login flow re-runs this function after the
      // cookie is set so authenticated flow still works.
      // Edge case: user cleared localStorage but kept the cookie — they get
      // shown the login screen and re-authenticate. Acceptable cost.
      // Except right after an emailed sign-in or "Forgot password?" link: it
      // has just set the session cookie, and this browser may hold no saved
      // user (signed out earlier), so ask the server.
      if (!storedUserData && !storedPermissions && !arrivedFromSignInLink()) {
        setAuthState({ user: null, permissions: [], isLoading: false, isAuthenticated: false });
        setError('');
        return;
      }

      if (storedUserData && storedPermissions) {
        try {
          const userData = JSON.parse(storedUserData);
          const permissions = JSON.parse(storedPermissions);

          // Verify the stored data is still valid by checking auth status
          try {
            const meResponse: any = await authService.getCurrentUser();

            // Helper to normalize /auth/me into our User shape
            const normalizeFromMe = (me: any, base: any): { user: User; perms: string[] } => {
              // Case 1: API returns { user, permissions }
              if (me?.user && me.user.id) {
                const u = me.user as User;
                const status = (u as any).user_status || u.status;
                const normalized: User = {
                  ...u,
                  id: (u as any).id,
                  status: status,
                  is_active:
                    typeof (u as any).is_active === 'boolean'
                      ? (u as any).is_active
                      : status
                        ? String(status).toLowerCase() === 'active'
                        : true,
                  locations: (u as any).locations || null,
                } as User;
                const perms = Array.isArray(me.permissions)
                  ? me.permissions
                  : Array.isArray((u as any).permissions)
                    ? (u as any).permissions
                    : [];
                return { user: normalized, perms };
              }

              // Case 2: API returns the me object like provided (user_id, user_status, etc.)
              if (me && (me.user_id || me.account_id || me.email)) {
                const status = me.user_status || base?.status || '';
                const locationsArr = Array.isArray(me.locations) ? me.locations : [];
                const effectiveLocationId = me.location_id || (locationsArr.length > 0 ? locationsArr[0]?.id : base?.location_id);
                const normalized: User = {
                  id: me.user_id || base?.id,
                  username: me.username || base?.username || '',
                  email: me.email || base?.email || '',
                  first_name: me.first_name ?? base?.first_name,
                  last_name: me.last_name ?? base?.last_name,
                  name: base?.name,
                  is_active: status
                    ? String(status).toLowerCase() === 'active'
                    : typeof base?.is_active === 'boolean'
                      ? base?.is_active
                      : true,
                  created_at: base?.created_at || '',
                  updated_at: base?.updated_at || '',
                  status: status,
                  avatar_url: base?.avatar_url ?? null,
                  phone_number: base?.phone_number ?? null,
                  bio: base?.bio ?? null,
                  location: base?.location ?? null,
                  website: base?.website ?? null,
                  account_id: me.account_id ?? base?.account_id,
                  location_id: effectiveLocationId,
                  locations:
                    locationsArr && locationsArr.length
                      ? locationsArr.map((l: any) => {
                          const locData = l.location || l;
                          const roleAcctName = l.roles?.[0]?.account_name;
                          return {
                            id: String(locData.id || l.id),
                            name: String(locData.name || l.name || locData.id),
                            account_id: locData.account_id || l.account_id || null,
                            account_name: l.account_name || l.account?.name || locData.account_name || roleAcctName || null,
                          };
                        })
                      : base?.locations || null,
                  role: base?.role,
                  accounts:
                    me.accounts && Array.isArray(me.accounts) && me.accounts.length > 0
                      ? me.accounts.map((a: any) => ({
                          id: a.id,
                          name: a.name,
                          is_owner: a.is_owner,
                          // Preserve account-scoped role assignments — the executive/accounts
                          // dashboard gate checks for ANY active role here.
                          roles: Array.isArray(a.roles) ? a.roles : [],
                        }))
                      : base?.accounts,
                  permissions: undefined,
                } as User;
                const perms: string[] = Array.isArray(me.permissions) ? me.permissions : [];
                return { user: normalized, perms };
              }

              // Fallback: assume me is already a User
              const u = me as User;
              const perms = Array.isArray((me as any)?.permissions) ? (me as any).permissions : [];
              return { user: u, perms };
            };

            const baseStored = storedUserData ? JSON.parse(storedUserData) : {};
            let { user: normalizedUser, perms: mePermissions } = normalizeFromMe(meResponse, baseStored);

            // Merge stored extended fields with freshest user
            const mergedUser = { ...baseStored, ...normalizedUser };

            // Persist refreshed data
            localStorage.setItem('user_data', JSON.stringify(mergedUser));
            localStorage.setItem('user_permissions', JSON.stringify(mePermissions || []));

            // Set location ID first to ensure it's available when Dashboard component renders
            try {
              setStoredLocationId((mergedUser as any).location_id || null);
            } catch {}

            // Pre-populate permission cache to prevent duplicate /auth/me call
            populatePermissionCache((mergedUser as any).location_id, mePermissions || []);

            setAuthState({
              user: mergedUser,
              permissions: mePermissions || [],
              isLoading: false,
              isAuthenticated: true,
            });
            setError('');
            return;
          } catch (apiError) {
            // Check if it's an authentication error (401, 403, or missing token)
            const errorMessage = apiError instanceof Error ? apiError.message : String(apiError);
            const isAuthError =
              errorMessage.includes('401') ||
              errorMessage.includes('403') ||
              errorMessage.includes('Unauthorized') ||
              errorMessage.includes('authentication token') ||
              errorMessage.includes('Missing authentication');

            if (isAuthError) {
              // Clear cached data and show login screen
              localStorage.removeItem('user_data');
              localStorage.removeItem('user_permissions');
              try {
                setStoredLocationId(null);
              } catch {}
              try {
                clearStoredAccountId();
              } catch {}
              setAuthState({
                user: null,
                permissions: [],
                isLoading: false,
                isAuthenticated: false,
              });
              // Don't show "session expired" error on first visit - just clear the error
              setError('');
              return;
            }

            // For other errors (network issues, etc.), use cached data
            const ensuredUser = userData as any;

            setAuthState({
              user: ensuredUser,
              permissions,
              isLoading: false,
              isAuthenticated: true,
            });
            setError('');
            return;
          }
        } catch (parseError) {
          // If parsing fails, clear stored data and continue with API call
          localStorage.removeItem('user_data');
          localStorage.removeItem('user_permissions');
          try {
            setStoredLocationId(null);
          } catch {}
          try {
            clearStoredAccountId();
          } catch {}
        }
      }

      // Fallback: get user from API (may lack extended fields)
      const meResponse: any = await authService.getCurrentUser();

      // Reuse the same normalizer for fallback path
      let { user: normalizedUser, perms: derivedPermissions } = (() => {
        // reuse the function inline (duplicate minimal to avoid hoisting issues)
        if (meResponse?.user && meResponse.user.id) {
          const u = meResponse.user as User;
          const status = (u as any).user_status || u.status;
          const normalized: User = {
            ...u,
            id: (u as any).id,
            status: status,
            is_active:
              typeof (u as any).is_active === 'boolean' ? (u as any).is_active : status ? String(status).toLowerCase() === 'active' : true,
          } as User;
          const perms = Array.isArray(meResponse.permissions)
            ? meResponse.permissions
            : Array.isArray((u as any).permissions)
              ? (u as any).permissions
              : [];
          return { user: normalized, perms };
        }
        if (meResponse && (meResponse.user_id || meResponse.account_id || meResponse.email)) {
          const status = meResponse.user_status || '';
          const locationsArr = Array.isArray(meResponse.locations) ? meResponse.locations : [];
          const effectiveLocationId = meResponse.location_id || (locationsArr.length > 0 ? locationsArr[0]?.id : undefined);
          const normalized: User = {
            id: meResponse.user_id,
            username: meResponse.username || '',
            email: meResponse.email || '',
            first_name: meResponse.first_name,
            last_name: meResponse.last_name,
            name: undefined,
            is_active: status ? String(status).toLowerCase() === 'active' : true,
            created_at: '',
            updated_at: '',
            status: status,
            phone_number: meResponse.phone_number || undefined,
            account_id: meResponse.account_id,
            location_id: effectiveLocationId,
            locations:
              locationsArr && locationsArr.length
                ? locationsArr.map((l: any) => {
                    const locData = l.location || l;
                    const roleAcctName = l.roles?.[0]?.account_name;
                    return {
                      id: String(locData.id || l.id),
                      name: String(locData.name || l.name || locData.id),
                      account_id: locData.account_id || l.account_id || null,
                      account_name: l.account_name || l.account?.name || locData.account_name || roleAcctName || null,
                    };
                  })
                : undefined,
            role: undefined,
            accounts: undefined,
            permissions: undefined,
          } as User;
          const perms: string[] = Array.isArray(meResponse.permissions) ? meResponse.permissions : [];
          return { user: normalized, perms };
        }
        const u = meResponse as User;
        const perms = Array.isArray((u as any)?.permissions) ? (u as any).permissions : [];
        return { user: u, perms };
      })();

      // Persist what we learned so subsequent refreshes work
      localStorage.setItem('user_data', JSON.stringify(normalizedUser));
      localStorage.setItem('user_permissions', JSON.stringify(derivedPermissions));

      // Set location ID first to ensure it's available when Dashboard component renders
      try {
        setStoredLocationId((normalizedUser as any).location_id || null);
      } catch {}

      // Pre-populate permission cache to prevent duplicate /auth/me call
      populatePermissionCache((normalizedUser as any).location_id, derivedPermissions);

      setAuthState({
        user: normalizedUser,
        permissions: derivedPermissions,
        isLoading: false,
        isAuthenticated: true,
      });
      setError('');
    } catch (error) {
      // Clear all stored data on auth failure
      localStorage.removeItem('user_data');
      localStorage.removeItem('user_permissions');
      try {
        setStoredLocationId(null);
      } catch {}
      try {
        clearStoredAccountId();
      } catch {}
      setAuthState({
        user: null,
        permissions: [],
        isLoading: false,
        isAuthenticated: false,
      });
    } finally {
      checkingAuthRef.current = false;
    }
  };

  const login = useCallback(async (credentials: LoginRequest): Promise<LoginResponse> => {
    try {
      setAuthState((prev) => ({ ...prev, isLoading: true }));
      setError('');

      const response = await authService.login(credentials);

      // Normalize user
      // The backend User entity no longer includes account_id directly;
      // populate from session.account_id (set by JWT claims).
      let normalizedUser: User = {
        ...response.user,
        account_id: response.user?.account_id || (response as any).session?.account_id || undefined,
      } as User;

      // Add account and locations data to user object for simplified dashboard access
      const fullResponse = response as any;

      if (fullResponse.account) {
        (normalizedUser as any).account = fullResponse.account;
      }
      if (fullResponse.locations && Array.isArray(fullResponse.locations)) {
        // Normalize locations to flat structure.
        // Login response wraps each location in a {location: {id, name, account_id, account}, roles, ...} object,
        // but consumers expect a flat {id, name, account_id, account_name} structure.
        // Preserve account_id and account_name for multi-account filtering and display in App.tsx.
        (normalizedUser as any).locations = fullResponse.locations.map((loc: any) => {
          const locationData = loc.location || loc;
          const accountData = locationData.account || loc.account;
          // Get account_name from roles if not available elsewhere (roles have account_name)
          const roleAccountName = loc.roles?.[0]?.account_name;

          return {
            id: String(locationData.id || locationData.location_id || loc.id || ''),
            name: String(locationData.name || locationData.location_name || loc.name || locationData.id || ''),
            account_id: locationData.account_id || loc.account_id || null,
            account_name: accountData?.name || locationData.account_name || loc.account_name || roleAccountName || null,
          };
        });

        // Use accounts from backend response if provided (multi-account support)
        if (fullResponse.accounts && Array.isArray(fullResponse.accounts) && fullResponse.accounts.length > 0) {
          (normalizedUser as any).accounts = fullResponse.accounts.map((a: any) => ({
            id: a.id,
            name: a.name,
            is_owner: a.is_owner,
            // Preserve account-scoped role assignments — executive/accounts dashboard gate reads these.
            roles: Array.isArray(a.roles) ? a.roles : [],
          }));
        } else {
          // Fallback: build unique accounts from locations
          const accountMap = new Map<string, string>();
          (normalizedUser as any).locations.forEach((loc: any) => {
            if (loc.account_id && !accountMap.has(loc.account_id)) {
              accountMap.set(loc.account_id, loc.account_name || 'Account');
            }
          });

          if (accountMap.size > 0) {
            (normalizedUser as any).accounts = Array.from(accountMap.entries()).map(([id, name]) => ({
              id,
              name,
            }));
          } else {
            (normalizedUser as any).accounts = [
              {
                id: fullResponse.account?.id || (normalizedUser as any).account_id,
                name: fullResponse.account?.name || 'Account',
              },
            ];
          }
        }
      }

      // Store both user data and permissions in localStorage for persistence
      // This ensures all user fields including account_id, location_id, role, etc. are preserved
      localStorage.setItem('user_data', JSON.stringify(normalizedUser));
      localStorage.setItem('user_permissions', JSON.stringify(response.permissions || (response.user as any)?.permissions || []));

      // Clear any previously selected account/location to ensure clean state for new user
      try {
        setStoredLocationId((normalizedUser as any).location_id || null);
      } catch {}
      try {
        clearStoredAccountId();
      } catch {} // Clear account selection to let the dashboard choose appropriate one

      // Set login grace period to prevent race conditions with subsequent API calls
      // This gives time for cookies to be fully processed before allowing 401s to trigger logout
      loginGracePeriodRef.current = true;
      gracePeriodFailureRef.current = false; // Reset failure tracking
      setTimeout(() => {
        loginGracePeriodRef.current = false;

        // Check if any 401s occurred during the grace period
        if (gracePeriodFailureRef.current) {
          console.warn('[useAuth] Auth failures occurred during grace period - retrying auth check');
          gracePeriodFailureRef.current = false;
          // Reset the hasChecked flag to allow a fresh auth check
          hasCheckedRef.current = false;
          checkingAuthRef.current = false;
          // Retry auth check after a short delay to give cookies time to settle
          setTimeout(() => {
            checkAuthStatus();
          }, 500);
        }
      }, 3000); // 3 second grace period

      setAuthState({
        user: normalizedUser,
        permissions: response.permissions || (response.user as any)?.permissions || [],
        isLoading: false,
        isAuthenticated: true,
      });

      return response;
    } catch (err) {
      // Clear stored data on login failure
      localStorage.removeItem('user_data');
      localStorage.removeItem('user_permissions');
      try {
        setStoredLocationId(null);
      } catch {}
      try {
        clearStoredAccountId();
      } catch {}
      // Forced-reset isn't really a "login failure" — the credentials were
      // valid; the server just refuses to issue a session until the user
      // sets a new password. Don't surface it in the global error banner;
      // let the LoginScreen catch the typed error and pivot to its reset UI.
      const forceReset = (err as any)?.code === 'FORCE_PASSWORD_RESET_REQUIRED';
      if (!forceReset) {
        let errorMessage = err instanceof Error ? err.message : 'Login failed';
        if (/body is disturbed|body is used|locked/i.test(errorMessage)) {
          errorMessage = 'Login failed. Please check your email/username and password.';
        }
        setError(errorMessage);
      } else {
        setError('');
      }
      setAuthState((prev) => ({
        ...prev,
        permissions: [],
        isLoading: false,
        isAuthenticated: false,
      }));
      throw err;
    }
  }, []);

  // Set auth state from a passwordless (OTP) verify response.
  // The backend already set the HTTP-only cookie; this syncs React state.
  const loginFromPasswordless = useCallback((response: any) => {
    // Build normalizedUser the same way login() does
    // Populate account_id from session since User entity no longer carries it
    let normalizedUser: any = {
      ...response.user,
      account_id: response.user?.account_id || response.session?.account_id || undefined,
    };

    if (response.locations && Array.isArray(response.locations)) {
      normalizedUser.locations = response.locations.map((loc: any) => {
        const locationData = loc.location || loc;
        const accountData = locationData.account || loc.account;
        const roleAccountName = loc.roles?.[0]?.account_name;
        return {
          id: String(locationData.id || locationData.location_id || loc.id || ''),
          name: String(locationData.name || locationData.location_name || loc.name || locationData.id || ''),
          account_id: locationData.account_id || loc.account_id || null,
          account_name: accountData?.name || locationData.account_name || loc.account_name || roleAccountName || null,
        };
      });

      const accountMap = new Map<string, string>();
      normalizedUser.locations.forEach((loc: any) => {
        if (loc.account_id && !accountMap.has(loc.account_id)) {
          accountMap.set(loc.account_id, loc.account_name || 'Account');
        }
      });
      if (accountMap.size > 0) {
        normalizedUser.accounts = Array.from(accountMap.entries()).map(([id, name]) => ({ id, name }));
      }
    }

    if (response.account) {
      normalizedUser.account = response.account;
    }

    const permissions = response.permissions || response.user?.permissions || [];

    localStorage.setItem('user_data', JSON.stringify(normalizedUser));
    localStorage.setItem('user_permissions', JSON.stringify(permissions));

    try {
      setStoredLocationId(normalizedUser.location_id || null);
    } catch {}
    try {
      clearStoredAccountId();
    } catch {}

    // Set login grace period (same as login())
    loginGracePeriodRef.current = true;
    gracePeriodFailureRef.current = false;
    setTimeout(() => {
      loginGracePeriodRef.current = false;
      if (gracePeriodFailureRef.current) {
        gracePeriodFailureRef.current = false;
        hasCheckedRef.current = false;
        checkingAuthRef.current = false;
        setTimeout(() => {
          checkAuthStatus();
        }, 500);
      }
    }, 3000);

    setAuthState({
      user: normalizedUser,
      permissions,
      isLoading: false,
      isAuthenticated: true,
    });
    setError('');
  }, []);

  const logout = useCallback(async () => {
    try {
      setAuthState((prev) => ({ ...prev, isLoading: true }));
      await authService.logout();
      // Clear all stored user data
      localStorage.removeItem('user_data');
      localStorage.removeItem('user_permissions');
      try {
        setStoredLocationId(null);
      } catch {}
      try {
        clearStoredAccountId();
      } catch {}
      setAuthState({
        user: null,
        permissions: [],
        isLoading: false,
        isAuthenticated: false,
      });
      setError('');
    } catch (err) {
      // Even if logout API fails, clear local data
      localStorage.removeItem('user_data');
      localStorage.removeItem('user_permissions');
      try {
        setStoredLocationId(null);
      } catch {}
      try {
        clearStoredAccountId();
      } catch {}
      const errorMessage = err instanceof Error ? err.message : 'Logout failed';
      setError(errorMessage);
      setAuthState((prev) => ({ ...prev, isLoading: false }));
    }
  }, []);

  const updateProfile = useCallback(
    async (updates: Partial<User> & { current_password?: string }) => {
      if (!authState.user) return;

      try {
        const result = await authService.updateUserProfile(authState.user.id, updates);

        if (result.credential_changed) {
          // Force re-login after credential change
          alert('Your login credentials have been updated. You will be logged out.');
          await logout();
          return;
        }

        // Update stored user data with the changes
        localStorage.setItem('user_data', JSON.stringify(result.user));

        setAuthState((prev) => ({
          ...prev,
          user: result.user,
        }));
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update profile';
        setError(errorMessage);
        throw err;
      }
    },
    [authState.user, logout]
  );

  // Debounce timer for permission refresh
  const permissionRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Cache for permission refresh (5 minute TTL)
  const permissionCacheRef = useRef<{ locationId: string; permissions: string[]; timestamp: number } | null>(null);
  const PERMISSION_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  // Populate permission cache from initial auth check to prevent duplicate /auth/me calls
  const populatePermissionCache = useCallback((locationId: string | undefined, permissions: string[]) => {
    if (locationId && permissions.length > 0) {
      permissionCacheRef.current = {
        locationId,
        permissions,
        timestamp: Date.now(),
      };
    }
  }, []);
  const isSystemAdmin = isSystemAdminCheck(authState.permissions);

  // Immediately update permissions in state, localStorage, and cache
  const updatePermissions = useCallback((permissions: string[], locationId?: string) => {
    setAuthState((prev) => {
      if (prev.permissions === permissions) {
        return prev;
      }
      return { ...prev, permissions };
    });
    localStorage.setItem('user_permissions', JSON.stringify(permissions));
    if (locationId) {
      permissionCacheRef.current = { locationId, permissions, timestamp: Date.now() };
    }
  }, []);

  // Refresh permissions for a specific location (called when user switches locations)
  const refreshPermissionsForLocation = useCallback(
    async (locationId: string) => {
      // Skip for system admins - they have all permissions regardless of location
      if (isSystemAdmin) {
        return;
      }

      const applyPermissionsUpdate = (nextPermissions: string[]) => {
        let updated = false;
        setAuthState((prev) => {
          if (prev.permissions === nextPermissions) {
            return prev;
          }
          updated = true;
          return {
            ...prev,
            permissions: nextPermissions,
          };
        });

        if (updated) {
          localStorage.setItem('user_permissions', JSON.stringify(nextPermissions));
        }
      };

      // Check cache - only use if for the same location and within TTL
      const now = Date.now();
      const cached = permissionCacheRef.current;
      if (cached && cached.locationId === locationId && now - cached.timestamp < PERMISSION_CACHE_TTL) {
        applyPermissionsUpdate(cached.permissions);
        return;
      }

      // Clear any pending debounce timer
      if (permissionRefreshTimerRef.current) {
        clearTimeout(permissionRefreshTimerRef.current);
      }

      // Debounce: wait 300ms before making the API call
      permissionRefreshTimerRef.current = setTimeout(async () => {
        try {
          // Use /auth/me which returns location-filtered permissions from JWT
          const url = `${getApiBaseUrl()}/auth/me`;
          const res = await fetch(url, { credentials: 'include' });

          if (res.ok) {
            const data = await res.json();
            const permissions = Array.isArray(data.permissions) ? data.permissions : [];

            if (permissions.length > 0) {
              // Update cache
              permissionCacheRef.current = {
                locationId,
                permissions,
                timestamp: Date.now(),
              };

              applyPermissionsUpdate(permissions);
            }
          }
          // Graceful degradation: keep existing permissions on error
        } catch {
          // Graceful degradation: keep existing permissions on error
        } finally {
          permissionRefreshTimerRef.current = null;
        }
      }, 300);
    },
    [isSystemAdmin]
  );

  return {
    ...authState,
    error,
    login,
    loginFromPasswordless,
    logout,
    updateProfile,
    updatePermissions,
    checkAuthStatus,
    refreshPermissionsForLocation,
  };
};
